'use client';

import { useCallback } from 'react';

import { FlatQuotationLine } from '@ravago/shared/page-data/models/elements';

import { resolveQuoteQuantity } from '../utils/quote-pricing/combined-pricing.utils';

import { usePricingQuantityState } from './use-pricing-quantity-state';

interface Params {
   quoteLines?: FlatQuotationLine[];
}

/**
 * Owns the Amount input for a quote-only pricing view (quote detail).
 *
 * The input is the single source of truth: only quoted quantities resolve, anything else falls
 * back to suggestions. Typing resolves through a debounce; clicking a quote line or a suggestion
 * (`selectQuantity`) commits immediately.
 */
export const useQuotePricingState = ({ quoteLines = [] }: Params) => {
   const resolve = useCallback(
      (quantity: number, preferredQuoteLineId: string | undefined) => resolveQuoteQuantity(quantity, { quoteLines, preferredQuoteLineId }),
      [quoteLines]
   );

   const { inputValue, onInputChange, hasSettled, resolution, selectQuantity } = usePricingQuantityState({ quoteLines, resolve });

   return {
      inputValue,
      onInputChange,
      hasSettled,
      resolution,
      selectQuantity,
      /** A single quote line leaves nothing to pick: the quantity input is redundant. */
      isSingleQuoteLine: quoteLines.length === 1
   };
};
